import type { Taxas } from '../Model/ITaxa.ts'
import type { ProcessingTypeKey } from '../Generated/ProcessingType.ts'
import { ProcessingTypes } from '../Generated/ProcessingType.ts'

export const processingTypeColorConfig: Record<ProcessingTypeKey, { accentBorder: string; accentBg: string; inlineBadge: string }> = {
  METABARCODING: {
    accentBorder: 'border-l-4 border-l-[var(--color-metabarcoding)]',
    accentBg: 'bg-metabarcoding',
    inlineBadge: 'bg-emerald-100 text-emerald-800 border border-emerald-200'
  },
  EDNA: {
    accentBorder: 'border-l-4 border-l-[var(--color-edna)]',
    accentBg: 'bg-edna',
    inlineBadge: 'bg-sky-100 text-sky-800 border border-sky-200'
  }
}

const colorConfig = (processingType?: ProcessingTypeKey | null) => processingTypeColorConfig[processingType ?? 'METABARCODING']

export const getProcessingTypeLabel = (processingType?: ProcessingTypeKey | null) => (processingType === 'EDNA' ? 'eDNA' : 'Metabarcoding')

export const getResultUnitLabel = (processingType?: ProcessingTypeKey | null) => (processingType === 'EDNA' ? 'ESV' : 'OTU')

export const getResultUnitLabelPlural = (processingType?: ProcessingTypeKey | null) => (processingType === 'EDNA' ? 'ESVs' : 'OTUs')

export const isOtuProcessingType = (processingType?: ProcessingTypeKey | null) => processingType === ProcessingTypes.METABARCODING.key

export const isEdnaProcessingType = (processingType?: ProcessingTypeKey | null) => processingType === ProcessingTypes.EDNA.key

export const processingTypeRules: Record<
  ProcessingTypeKey,
  {
    allowedTaxa: readonly Taxas[] | null
  }
> = {
  METABARCODING: {
    allowedTaxa: null
  },
  EDNA: {
    allowedTaxa: ['16S_VERTEBRATES', '12S_FISH']
  }
}

export const getAllowedTargetTaxaForProcessingType = (processingType?: ProcessingTypeKey | null) =>
  processingTypeRules[processingType ?? 'METABARCODING'].allowedTaxa

export const isProcessingTypeTaxaCombinationValid = (processingType?: ProcessingTypeKey | null, targetTaxonomy?: Taxas | null) => {
  const allowedTaxa = getAllowedTargetTaxaForProcessingType(processingType)
  if (allowedTaxa === null) {
    return true
  }
  return !!targetTaxonomy && allowedTaxa.includes(targetTaxonomy)
}

export const getProcessingTypeTaxaValidationMessage = (processingType?: ProcessingTypeKey | null) => {
  if (processingType === 'EDNA') {
    return 'eDNA orders are currently only available for 16S - Vertebrates and 12S - Vertebrates.'
  }
  return 'This processing type is not available for the selected target taxon.'
}

export const getProcessingTypeAccentBorder = (processingType?: ProcessingTypeKey | null) => colorConfig(processingType).accentBorder

export const getProcessingTypeAccentBg = (processingType?: ProcessingTypeKey | null) => colorConfig(processingType).accentBg

export const getProcessingTypeInlineBadgeStyle = (processingType?: ProcessingTypeKey | null) => colorConfig(processingType).inlineBadge
